import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const SERVICES_HOUSE = [
  '전기 및 조명 점검·교체',
  '수처리·위생 배관 보수',
  '냉·난방 환기 시스템',
  '내·외부 마감재 유지',
  '조경·외부시설 관리',
  '소방·보안 설비 점검',
  '조경 리모델링',
];

const SERVICES_GRAVE = [
  '예초·주변 정리',
  '식생·토양 관리',
  '석물 시설물 세척·보수',
  '의례 대행·모니터링',
];

export default function MobileLanding({ onShowReport }: { onShowReport?: () => void }) {
  const navigate = useNavigate();
  const [activeService, setActiveService] = useState<'house' | 'grave' | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', service: '벌초 대행' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const pageUrl = `${siteUrl}/mobile`;
    const pageTitle = '진주 벌초·정원관리 모바일 상담 – 홈&그레이브';
    const pageDesc = '경남 진주 전원주택 정원관리·산소 벌초 대행. GPS 면적 측정, 드론 촬영 보고, 21개 투명 요인 견적. 무료 견적 신청 48시간 내 답변.';

    // 페이지 타이틀 업데이트
    document.title = pageTitle;

    // canonical 업데이트
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    const prevCanonical = canonical.href;
    canonical.href = pageUrl;

    // meta description 업데이트
    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const prevDesc = metaDesc?.content ?? '';
    if (metaDesc) metaDesc.content = pageDesc;

    // OG 태그 업데이트
    const ogUrl = document.querySelector<HTMLMetaElement>('meta[property="og:url"]');
    const prevOgUrl = ogUrl?.content ?? '';
    if (ogUrl) ogUrl.content = pageUrl;

    const ogTitle = document.querySelector<HTMLMetaElement>('meta[property="og:title"]');
    const prevOgTitle = ogTitle?.content ?? '';
    if (ogTitle) ogTitle.content = pageTitle;

    const ogDesc = document.querySelector<HTMLMetaElement>('meta[property="og:description"]');
    const prevOgDesc = ogDesc?.content ?? '';
    if (ogDesc) ogDesc.content = pageDesc;

    // Twitter 태그 업데이트
    const twUrl = document.querySelector<HTMLMetaElement>('meta[name="twitter:url"]');
    const prevTwUrl = twUrl?.content ?? '';
    if (twUrl) twUrl.content = pageUrl;

    const twTitle = document.querySelector<HTMLMetaElement>('meta[name="twitter:title"]');
    const prevTwTitle = twTitle?.content ?? '';
    if (twTitle) twTitle.content = pageTitle;

    const twDesc = document.querySelector<HTMLMetaElement>('meta[name="twitter:description"]');
    const prevTwDesc = twDesc?.content ?? '';
    if (twDesc) twDesc.content = pageDesc;

    const schemaData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "LocalBusiness",
          "@id": `${siteUrl}/#localbusiness`,
          "name": "홈&그레이브",
          "alternateName": "Home & Grave",
          "description": "경남 진주 지역 전원주택 정원관리, 제초, 계절별 유지보수, 산소 벌초 대행 전문 서비스. GPS 면적 측정, 드론 촬영 보고, 21개 투명 요인 견적 제공.",
          "url": siteUrl,
          "telephone": "010-9879-8375",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "문산읍",
            "addressLocality": "진주시",
            "addressRegion": "경상남도",
            "postalCode": "52828",
            "addressCountry": "KR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "35.1796",
            "longitude": "128.1076"
          },
          "hasMap": "https://maps.google.com/?q=35.1796,128.1076",
          "areaServed": [
            { "@type": "City", "name": "진주시" },
            { "@type": "State", "name": "경상남도" }
          ],
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
              "opens": "08:00",
              "closes": "18:00"
            }
          ],
          "priceRange": "₩₩",
          "sameAs": []
        },
        {
          "@type": "WebSite",
          "@id": `${siteUrl}/#website`,
          "url": siteUrl,
          "name": "홈&그레이브",
          "description": "경남 진주 전원주택 정원관리·산소 벌초 대행 전문",
          "inLanguage": "ko-KR"
        },
        {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          "url": pageUrl,
          "name": pageTitle,
          "isPartOf": { "@id": `${siteUrl}/#website` },
          "about": { "@id": `${siteUrl}/#localbusiness` },
          "description": pageDesc,
          "inLanguage": "ko-KR",
          "breadcrumb": { "@id": `${pageUrl}#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${pageUrl}#breadcrumb`,
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "홈",
              "item": siteUrl
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "모바일 상담",
              "item": pageUrl
            }
          ]
        },
        {
          "@type": "Service",
          "name": "산소 벌초 대행",
          "provider": { "@id": `${siteUrl}/#localbusiness` },
          "areaServed": { "@type": "City", "name": "진주시" },
          "description": "경남 진주 지역 산소 벌초 대행 서비스. GPS 면적 측정 및 드론 촬영 보고 제공.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "KRW",
            "availability": "https://schema.org/InStock"
          }
        },
        {
          "@type": "Service",
          "name": "전원주택 정원관리",
          "provider": { "@id": `${siteUrl}/#localbusiness` },
          "areaServed": { "@type": "City", "name": "진주시" },
          "description": "경남 진주 전원주택 정원 관리, 제초, 계절별 유지보수 서비스.",
          "offers": {
            "@type": "Offer",
            "priceCurrency": "KRW",
            "availability": "https://schema.org/InStock"
          }
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-mobile';
    script.text = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      // 원래 값으로 복원
      document.title = '진주 전원주택 정원관리·산소 벌초 전문 홈&그레이브';
      if (canonical) canonical.href = prevCanonical;
      if (metaDesc) metaDesc.content = prevDesc;
      if (ogUrl) ogUrl.content = prevOgUrl;
      if (ogTitle) ogTitle.content = prevOgTitle;
      if (ogDesc) ogDesc.content = prevOgDesc;
      if (twUrl) twUrl.content = prevTwUrl;
      if (twTitle) twTitle.content = prevTwTitle;
      if (twDesc) twDesc.content = prevTwDesc;

      const existing = document.getElementById('schema-mobile');
      if (existing) document.head.removeChild(existing);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(false);
    try {
      const body = new URLSearchParams();
      body.append('name', form.name);
      body.append('phone', form.phone);
      body.append('service', form.service);

      const res = await fetch('https://readdy.ai/api/form/d6pcdiea739gopmlgorg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      await supabase.from('quote_requests').insert({
        name: form.name,
        phone: form.phone,
        service: form.service,
        status: 'pending',
      });

      if (res.ok) {
        setSubmitted(true);
        setForm({ name: '', phone: '', service: '벌초 대행' });
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1f3c', fontFamily: 'Noto Sans KR, sans-serif' }}>

      {/* ── 상단 헤더 ── */}
      <header style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        background: 'rgba(13,31,60,0.97)',
        borderBottom: '1px solid rgba(201,162,39,0.25)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '12px 16px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '22px' }}>🏡</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>홈 &amp; 그레이브</span>
        </div>
        <a
          href="tel:010-9879-8375"
          style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '8px 16px', borderRadius: '999px',
            background: 'linear-gradient(135deg, #c9a227, #b8860b)',
            color: '#0d1f3c', fontWeight: 700, fontSize: '14px',
            textDecoration: 'none', whiteSpace: 'nowrap',
          }}
        >
          <i className="ri-phone-fill" style={{ fontSize: '14px' }} />
          전화 상담
        </a>
      </header>

      {/* ── 히어로 섹션 ── */}
      <section style={{
        paddingTop: '80px',
        background: 'linear-gradient(160deg, #0d1f3c 0%, #152a52 60%, #111e3a 100%)',
        padding: '80px 20px 40px',
      }}>
        <div style={{
          display: 'inline-block', padding: '5px 14px', borderRadius: '999px',
          background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.4)',
          color: '#c9a227', fontWeight: 700, fontSize: '12px', marginBottom: '16px',
        }}>
          ✨ 진주 지역 프리미엄 공간 케어
        </div>
        <h1 style={{
          color: '#ffffff', fontWeight: 800, fontSize: '26px',
          lineHeight: 1.4, marginBottom: '12px', letterSpacing: '-0.04em',
        }}>
          집과 산소를 위한<br />
          <span style={{ color: '#c9a227' }}>과학적·정성스러운</span> 관리
        </h1>
        <p style={{
          color: 'rgba(200,216,240,0.75)', fontSize: '14px',
          lineHeight: 1.7, marginBottom: '28px',
        }}>
          위성 데이터·드론 기술로 관리하는<br />
          진주 전원주택 &amp; 산소 전문 서비스
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <a
            href="#quote-form"
            onClick={(e) => { e.preventDefault(); document.getElementById('quote-form')?.scrollIntoView({ behavior: 'smooth' }); }}
            style={{
              display: 'block', textAlign: 'center',
              padding: '16px', borderRadius: '14px',
              background: 'linear-gradient(135deg, #c9a227, #b8860b)',
              color: '#0d1f3c', fontWeight: 900, fontSize: '17px',
              textDecoration: 'none', cursor: 'pointer',
            }}
          >
            무료 견적 신청하기 →
          </a>
          <a
            href="tel:010-9879-8375"
            style={{
              display: 'block', textAlign: 'center',
              padding: '16px', borderRadius: '14px',
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(201,162,39,0.35)',
              color: '#ffffff', fontWeight: 700, fontSize: '16px',
              textDecoration: 'none',
            }}
          >
            <i className="ri-phone-line" style={{ marginRight: '8px' }} />
            010-9879-8375 전화하기
          </a>

          {/* ── 작업결과 리포트 확인 버튼 (히어로 인라인) ── */}
          <a
            href="/work-report/auth"
            style={{
              display: 'block', textAlign: 'center',
              padding: '16px', borderRadius: '14px',
              background: '#1A4D2E',
              border: '2px solid #2d6a4f',
              color: '#ffffff', fontWeight: 700, fontSize: '16px',
              textDecoration: 'none',
            }}
          >
            📋 작업 결과 리포트 확인
          </a>
        </div>
      </section>

      {/* ── 서비스 섹션 ── */}
      <section style={{
        background: 'linear-gradient(180deg, #111e3a 0%, #0d1f3c 100%)',
        padding: '40px 20px',
      }}>
        <p style={{ color: '#c9a227', fontSize: '11px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '8px' }}>Services</p>
        <h2 style={{ color: '#fff', fontWeight: 900, fontSize: '22px', marginBottom: '6px', letterSpacing: '-0.03em' }}>제공 서비스</h2>
        <p style={{ color: 'rgba(200,216,240,0.6)', fontSize: '13px', marginBottom: '24px', lineHeight: 1.6 }}>항목을 탭하면 상세 내용을 확인할 수 있습니다</p>

        {/* 주택 카드 */}
        <div
          onClick={() => setActiveService(activeService === 'house' ? null : 'house')}
          style={{
            marginBottom: '12px', borderRadius: '16px', overflow: 'hidden',
            border: activeService === 'house' ? '1px solid rgba(45,106,79,0.7)' : '1px solid rgba(45,106,79,0.35)',
            background: activeService === 'house'
              ? 'linear-gradient(135deg, rgba(45,106,79,0.25) 0%, rgba(13,31,60,0.9) 100%)'
              : 'rgba(255,255,255,0.05)',
            cursor: 'pointer',
          }}
        >
          <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '28px' }}>🏠</span>
              <div>
                <div style={{ color: '#ffffff', fontWeight: 900, fontSize: '17px', marginBottom: '3px' }}>주택 유지보수관리</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>전기·수처리·냉난방 등 7가지</div>
              </div>
            </div>
            <i
              className={activeService === 'house' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
              style={{ color: '#c9a227', fontSize: '22px' }}
            />
          </div>
          {activeService === 'house' && (
            <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(45,106,79,0.25)' }}>
              <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {SERVICES_HOUSE.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="ri-checkbox-circle-fill" style={{ color: '#2d6a4f', fontSize: '16px' }} />
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 산소 카드 */}
        <div
          onClick={() => setActiveService(activeService === 'grave' ? null : 'grave')}
          style={{
            borderRadius: '16px', overflow: 'hidden',
            border: activeService === 'grave' ? '1px solid rgba(201,162,39,0.7)' : '1px solid rgba(201,162,39,0.3)',
            background: activeService === 'grave'
              ? 'linear-gradient(135deg, rgba(92,74,53,0.35) 0%, rgba(13,31,60,0.9) 100%)'
              : 'rgba(255,255,255,0.05)',
            cursor: 'pointer',
          }}
        >
          <div style={{ padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{ fontSize: '28px' }}>🌿</span>
              <div>
                <div style={{ color: '#ffffff', fontWeight: 900, fontSize: '17px', marginBottom: '3px' }}>산소 벌초</div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>예초·식생·석물·의례 대행 4가지</div>
              </div>
            </div>
            <i
              className={activeService === 'grave' ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}
              style={{ color: '#c9a227', fontSize: '22px' }}
            />
          </div>
          {activeService === 'grave' && (
            <div style={{ padding: '0 20px 20px', borderTop: '1px solid rgba(201,162,39,0.2)' }}>
              <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {SERVICES_GRAVE.map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <i className="ri-checkbox-circle-fill" style={{ color: '#c9a227', fontSize: '16px' }} />
                    </div>
                    <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '14px' }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ── 차별점 3가지 ── */}
      <section style={{
        background: 'linear-gradient(160deg, #0d1f3c 0%, #152a52 100%)',
        padding: '40px 20px',
      }}>
        <p style={{ color: '#c9a227', fontSize: '11px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '8px' }}>Why Us</p>
        <h2 style={{ color: '#fff', fontWeight: 900, fontSize: '22px', marginBottom: '24px', letterSpacing: '-0.03em' }}>홈&amp;그레이브만의 차이</h2>

        {[
          { icon: 'ri-satellite-line', title: '10년 위성 사진 분석', desc: '지형·식생 변화를 데이터로 파악해 정확한 견적을 제공합니다' },
          { icon: 'ri-drone-line', title: '드론 촬영 작업 보고서', desc: '작업 전후 드론 사진과 전문가 소견을 SMS로 즉시 전달합니다' },
          { icon: 'ri-file-list-3-line', title: '21개 투명 견적 항목', desc: '임의 가격 없이 객관적 기준으로만 산출된 정찰제 견적입니다' },
        ].map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'flex-start', gap: '16px',
            padding: '18px', borderRadius: '14px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(201,162,39,0.15)',
            marginBottom: '12px',
          }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '12px',
              background: 'rgba(201,162,39,0.12)',
              border: '1px solid rgba(201,162,39,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <i className={item.icon} style={{ color: '#c9a227', fontSize: '20px' }} />
            </div>
            <div>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: '15px', marginBottom: '5px' }}>{item.title}</div>
              <div style={{ color: 'rgba(200,216,240,0.65)', fontSize: '13px', lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </section>

      {/* ── 간단 견적 신청 폼 ── */}
      <section id="quote-form" style={{
        background: 'linear-gradient(180deg, #111e3a 0%, #0d1f3c 100%)',
        padding: '40px 20px',
      }}>
        <p style={{ color: '#c9a227', fontSize: '11px', fontWeight: 700, letterSpacing: '0.3em', textTransform: 'uppercase', marginBottom: '8px' }}>Free Quote</p>
        <h2 style={{ color: '#fff', fontWeight: 900, fontSize: '22px', marginBottom: '6px', letterSpacing: '-0.03em' }}>무료 견적 신청</h2>
        <p style={{ color: 'rgba(200,216,240,0.6)', fontSize: '13px', marginBottom: '24px', lineHeight: 1.6 }}>
          48시간 내 연락드립니다
        </p>

        {submitted ? (
          <div style={{
            padding: '32px 20px', borderRadius: '16px', textAlign: 'center',
            background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.3)',
          }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✅</div>
            <div style={{ color: '#c9a227', fontWeight: 900, fontSize: '18px', marginBottom: '8px' }}>신청 완료!</div>
            <div style={{ color: 'rgba(200,216,240,0.7)', fontSize: '14px', lineHeight: 1.6 }}>
              48시간 내 담당자가 연락드립니다.<br />급하신 경우 전화 주세요.
            </div>
            <a href="tel:010-9879-8375" style={{
              display: 'inline-block', marginTop: '20px',
              padding: '12px 28px', borderRadius: '999px',
              background: 'linear-gradient(135deg, #c9a227, #b8860b)',
              color: '#0d1f3c', fontWeight: 700, fontSize: '15px',
              textDecoration: 'none',
            }}>
              010-9879-8375 전화하기
            </a>
          </div>
        ) : (
          <form id="mobile-quote-form" data-readdy-form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div>
              <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>이름 *</label>
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
                placeholder="홍길동"
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(201,162,39,0.25)',
                  color: '#fff', fontSize: '15px', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>연락처 *</label>
              <input
                type="tel"
                name="phone"
                value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                required
                placeholder="010-1234-5678"
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '12px',
                  background: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(201,162,39,0.25)',
                  color: '#fff', fontSize: '15px', outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <div>
              <label style={{ display: 'block', color: 'rgba(255,255,255,0.7)', fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>서비스 종류 *</label>
              <select
                name="service"
                value={form.service}
                onChange={e => setForm(f => ({ ...f, service: e.target.value }))}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '12px',
                  background: '#0d1f3c',
                  border: '1px solid rgba(201,162,39,0.25)',
                  color: '#fff', fontSize: '15px', outline: 'none',
                  boxSizing: 'border-box', cursor: 'pointer',
                }}
              >
                <optgroup label="산소·선산 관리">
                  <option value="벌초 대행">벌초 대행</option>
                  <option value="묶음 벌초">묶음 벌초 할인</option>
                  <option value="석축/보수">전문 석축/보수</option>
                </optgroup>
                <optgroup label="전원주택 관리">
                  <option value="전원주택 정원관리">정원 관리</option>
                  <option value="전원주택 제초">제초 서비스</option>
                  <option value="전원주택 계절유지보수">계절별 유지보수</option>
                  <option value="전원주택 연간구독">연간 구독 패키지</option>
                </optgroup>
              </select>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', padding: '16px', borderRadius: '14px',
                background: submitting ? 'rgba(201,162,39,0.5)' : 'linear-gradient(135deg, #c9a227, #b8860b)',
                color: '#0d1f3c', fontWeight: 900, fontSize: '17px',
                border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                marginTop: '4px',
              }}
            >
              {submitting ? '전송 중...' : '무료 견적 신청하기'}
            </button>

            {error && (
              <div style={{
                padding: '12px 16px', borderRadius: '10px',
                background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5', fontSize: '13px', textAlign: 'center',
              }}>
                전송 중 오류가 발생했습니다. 전화로 문의해주세요.
              </div>
            )}
          </form>
        )}
      </section>

      {/* ── 푸터 ── */}
      <footer style={{
        background: '#0a1628',
        borderTop: '1px solid rgba(201,162,39,0.2)',
        padding: '32px 20px 140px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ fontSize: '20px' }}>🏡</span>
          <span style={{ color: '#c9a227', fontWeight: 700, fontSize: '16px' }}>홈&amp;그레이브</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
            <i className="ri-phone-line" style={{ color: '#c9a227' }} />
            010-9879-8375
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
            <i className="ri-map-pin-line" style={{ color: '#c9a227' }} />
            경상남도 진주시 문산읍
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.55)', fontSize: '13px' }}>
            <i className="ri-time-line" style={{ color: '#c9a227' }} />
            운영시간 08:00 – 18:00 (연중무휴)
          </div>
        </div>

        {/* PC 홈페이지 링크 */}
        <div style={{
          padding: '16px', borderRadius: '14px',
          background: 'rgba(201,162,39,0.08)',
          border: '1px solid rgba(201,162,39,0.25)',
          marginBottom: '20px',
        }}>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', marginBottom: '10px' }}>
            서비스 상세, 가격, 사례 등 전체 내용은 PC 홈페이지에서 확인하세요
          </div>
          <button
            onClick={() => { window.location.href = '/?view=desktop'; }}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              width: '100%',
              padding: '12px', borderRadius: '10px',
              background: 'rgba(201,162,39,0.15)',
              border: '1px solid rgba(201,162,39,0.4)',
              color: '#c9a227', fontWeight: 700, fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <i className="ri-computer-line" style={{ fontSize: '16px' }} />
            PC 전체 홈페이지 보기
          </button>
        </div>

        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '11px', textAlign: 'center' }}>
          © 2024 홈&amp;그레이브. All rights reserved.
        </div>
      </footer>

      {/* ── 작업결과 리포트 확인 버튼 (하단 고정, 보조용) ── */}
      <a
        href="/work-report/auth"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 2147483647,
          display: 'block',
          background: '#0a1628',
          borderTop: '2px solid #2d6a4f',
          padding: '12px 16px',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom, 20px))',
          textDecoration: 'none',
          textAlign: 'center',
          color: '#ffffff',
          fontWeight: 700,
          fontSize: '16px',
          fontFamily: 'Noto Sans KR, sans-serif',
          letterSpacing: '-0.01em',
          boxSizing: 'border-box',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <span style={{
          display: 'block',
          background: '#1A4D2E',
          border: '2px solid #2d6a4f',
          borderRadius: '12px',
          padding: '14px 16px',
          color: '#ffffff',
        }}>
          📋 작업 결과 리포트 확인
        </span>
      </a>

    </div>
  );
}
